import { useState } from 'react';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { Badge } from '@/components/Badge';
import Icon from '@/components/Icon/Icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { HStack } from '../Layout';
type AccordionBodyProps = PropsWithChildren<{
  open: boolean;
  variant?: 'default' | 'simple';
}>;
function AccordionBody({
  open,
  children,
  variant = 'default'
}: AccordionBodyProps) {
  if (variant === 'simple') {
    return <div className={cn('flex transition-[max-height,opacity]', !open && 'max-h-0 opacity-0')}>
        <div className="flex w-full flex-col gap-1 py-1">{children}</div>
      </div>;
  }
  return <div className={cn('flex max-h-40 transition-[max-height,opacity]', !open && 'max-h-0 opacity-0')}>
      <div className="-mr-3 flex w-9 shrink-0 justify-center py-1">
        <div role="presentation" className="min-h-full w-px bg-border" />
      </div>
      <div className="flex w-full flex-col gap-1 py-1">{children}</div>
    </div>;
}
type AccordionToggleProps = {
  open: boolean;
  onClick: () => void;
  icon?: ReactElement;
  label?: string;
  inDevelopment?: boolean;
  className?: string;
};
function AccordionToggle({
  open,
  onClick,
  icon,
  label,
  inDevelopment,
  className
}: AccordionToggleProps) {
  return <Button variant="ghost" className="relative w-full justify-start p-0" onClick={onClick} disabled={inDevelopment}>
      <HStack className={cn('w-full items-center justify-between', className)}>
        <div className="flex-1 min-w-0">{icon || label}</div>
        {inDevelopment ? <Badge variant="destructiveMuted">
            <Icon iconName="Hammer" text="In development" colorVariant="primary" textColor="text-primary" />
          </Badge> : <Icon iconName="ChevronDownIcon" className={cn('m-[10px] shrink-0 transition-transform', open && 'rotate-180')} />}
      </HStack>
    </Button>;
}
type AccordionButtonProps = {
  icon?: ReactElement;
  label?: string;
  inDevelopment?: boolean;
  className?: string;
  children?: ReactNode;
  variant?: 'default' | 'simple';
  defaultOpen?: boolean;
};
export function AccordionButton({
  icon,
  label,
  inDevelopment,
  className,
  children,
  variant = 'default',
  defaultOpen = false
}: AccordionButtonProps) {
  const [open, setOpen] = useState(defaultOpen);
  const handleToggle = () => {
    setOpen(!open);
  };
  return <>
      <AccordionToggle open={open} onClick={handleToggle} icon={icon} label={label} inDevelopment={inDevelopment} className={className} />
      <AccordionBody open={open} variant={variant}>
        {children}
      </AccordionBody>
    </>;
}